import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import PreviewIcon from '@mui/icons-material/Preview';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import client from 'client';
import { buildCustomerInvoicesPdfBlob, printCustomerInvoicesPdf } from './CustomerInvoicesPrintPdf';
import CustomerInvoicesEmailModal from './CustomerInvoicesEmailModal';
import { changeCurrency, constants, formatter, PERMISSION_MODEL } from '../../../constants';
import { hasPermission } from '../loadz/utils';
import { showMessage } from 'app/store/fuse/messageSlice';
import OriginalJobLabel from 'app/main/loadz/components/OriginalJobLabel';
import ModalGenerateInvoices from 'app/main/Invoices/Invoices/Modals/ModalGenerateInvoices';
import ModalToInvoice from 'app/main/Invoices/Invoices/Modals/ModalToInvoice';
import ModalToApplyPayments from 'app/main/Customers/NewCustomersComponents/ModalToApplyPayments';

const headerCellSx = {
  borderColor: 'black',
  borderBottom: 2,
  backgroundColor: 'white',
  ':hover': { cursor: 'pointer' },
};

function moneyWithSign(value) {
  const amount = String(value ?? '').replace(/\$/g, '').trim();
  return amount ? `$${amount}` : '';
}

function csvFilenameFromResponse(response, fallback) {
  const header = response?.headers?.['content-disposition'] || '';
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(header);
  const raw = match?.[1] || match?.[2];
  if (!raw) return fallback;
  try {
    return decodeURIComponent(raw);
  } catch (error) {
    return raw;
  }
}

async function saveBlobWithDialog(blob, filename) {
  if (typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'CSV', accept: { 'text/csv': ['.csv'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') return false;
    }
  }
  saveAs(blob, filename);
  return true;
}

function parseMoney(value) {
  const number = parseFloat(String(value ?? '').replace(/\$|,/g, ''));
  return Number.isFinite(number) ? number : 0;
}

function invoiceBelongsToJob(invoice, job) {
  const jobId = job?.job_info_id;
  if (jobId != null && invoice?.jobId != null && String(invoice.jobId) === String(jobId)) {
    return true;
  }
  const fullNo =
    job?.company_id != null && job?.job_no != null ? `${job.company_id}-${job.job_no}` : '';
  const label = invoice?.job != null ? String(invoice.job) : '';
  if (fullNo && label === fullNo) return true;
  if (job?.job_no != null && label === String(job.job_no)) return true;
  return false;
}

function groupsFromResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.job_no_search)) return data.job_no_search;
  if (Array.isArray(data?.invoice_no_search)) return data.invoice_no_search;
  return [];
}

function flattenInvoices(groups) {
  const invoices = [];
  groups.forEach((group) => {
    (group?.invoices || []).forEach((invoice) => invoices.push(invoice));
  });
  return invoices;
}

async function fetchGroups({ compacctnum, customerId, status, jobSearch, page }) {
  const search =
    jobSearch == null || jobSearch === '' ? 'null' : encodeURIComponent(String(jobSearch));
  const result = await client.get(
    `/invoices/customer/${compacctnum}/${customerId}/${status}/null/${search}`,
    { params: { page } }
  );
  return groupsFromResponse(result?.data);
}

async function loadJobInvoices({ compacctnum, customerId, status, job, jobSearch }) {
  const matched = [];
  const seen = new Set();
  for (let page = 1; page <= 15; page += 1) {
    const groups = await fetchGroups({ compacctnum, customerId, status, jobSearch, page });
    if (!groups.length) break;
    let newCount = 0;
    flattenInvoices(groups).forEach((invoice) => {
      const key = String(invoice.invoiceId ?? invoice.invoice);
      if (seen.has(key)) return;
      seen.add(key);
      newCount += 1;
      if (invoiceBelongsToJob(invoice, job)) matched.push(invoice);
    });
    if (newCount === 0) break;
  }
  return matched;
}

function sortInvoices(rows, column, order) {
  const direction = order === 'desc' ? -1 : 1;
  const sorted = [...rows];
  sorted.sort((a, b) => {
    if (column === 'invoice') return (Number(a.invoice) - Number(b.invoice)) * direction;
    if (column === 'job') return String(a.job ?? '').localeCompare(String(b.job ?? '')) * direction;
    if (column === 'dueDate') {
      return (new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()) * direction;
    }
    if (column === 'invoiceAmount') {
      return (parseMoney(a.total) - parseMoney(b.total)) * direction;
    }
    if (column === 'balancePending') return (parseMoney(a.balance) - parseMoney(b.balance)) * direction;
    return 0;
  });
  return sorted;
}

function JobCustomerInvoicesDialog({ open, onClose, job, compacctnum, propData }) {
  const { t } = useTranslation('navigation');
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.loadz_auth?.AuthReducer?.user);
  const [printCompany, setPrintCompany] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sorting, setSorting] = useState({ column: '', order: 'asc' });
  const [tickets, setTickets] = useState([]);
  const [info, setInfo] = useState({});
  const [payOpen, setPayOpen] = useState(false);
  const [dataToPayments, setDataToPayments] = useState([]);
  const [showInvoiceInApply, setShowInvoiceInApply] = useState(new Map());
  const [payingId, setPayingId] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [printChoiceOpen, setPrintChoiceOpen] = useState(false);
  const [printScope, setPrintScope] = useState('both');
  const [printing, setPrinting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const openModalViewInvoice = useRef(null);
  const openModalEditInvoice = useRef(null);
  const setMessage = useRef(null);
  const rechargedAfterChanges = useRef(null);
  const reloadRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const userId = user?.user_id;
    if (!userId) return undefined;
    let cancelled = false;
    const loadCompany = async () => {
      try {
        const result = await client.get(`/company/${userId}`);
        if (!cancelled) setPrintCompany(result?.data || null);
      } catch (error) {
        if (!cancelled) setPrintCompany(null);
      }
    };
    loadCompany();
    return () => {
      cancelled = true;
    };
  }, [open, user?.user_id]);

  const sessionCompany = printCompany || user?.company || null;
  const companyLogoKey = sessionCompany?.logo;
  const companyLogoSrc = companyLogoKey
    ? String(companyLogoKey).includes('http')
      ? companyLogoKey
      : `${constants.URLLOCAL}/aws/signed?image=${companyLogoKey}`
    : '';
  const companyAddress = [
    sessionCompany?.compaddress,
    sessionCompany?.compcity,
    sessionCompany?.compstate,
    sessionCompany?.compzip,
  ]
    .filter((part) => part != null && String(part).trim() !== '')
    .join(', ');

  const customerId = job?.client_id;
  const jobNo = job?.job_no;
  const jobLabel =
    job?.company_id != null && job?.job_no != null ? `${job.company_id}-${job.job_no}` : job?.job_no;

  const notify = (message, color) => {
    dispatch(
      showMessage({
        message: <Typography>{message}</Typography>,
        autoHideDuration: 6000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        variant: color,
      })
    );
  };

  setMessage.current = notify;

  const load = async (isCancelled = () => false) => {
    if (!compacctnum || !customerId || jobNo == null) {
      if (!isCancelled()) {
        setOpenInvoices([]);
        setPaidInvoices([]);
      }
      return;
    }
    if (!isCancelled()) setLoading(true);
    try {
      const fullNo = job?.company_id != null ? `${job.company_id}-${job.job_no}` : '';
      const fetchStatus = async (status) => {
        let rows = await loadJobInvoices({
          compacctnum,
          customerId,
          status,
          job,
          jobSearch: jobNo,
        });
        if (!isCancelled() && !rows.length && fullNo && fullNo !== String(jobNo)) {
          rows = await loadJobInvoices({
            compacctnum,
            customerId,
            status,
            job,
            jobSearch: fullNo,
          });
        }
        return rows;
      };
      const [openRows, paidRows] = await Promise.all([fetchStatus(1), fetchStatus(0)]);
      if (isCancelled()) return;
      setOpenInvoices(openRows);
      setPaidInvoices(paidRows);
      setSorting({ column: '', order: 'asc' });
    } catch (error) {
      if (isCancelled()) return;
      setOpenInvoices([]);
      setPaidInvoices([]);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  reloadRef.current = load;
  rechargedAfterChanges.current = () => {
    if (reloadRef.current) reloadRef.current();
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [open, customerId, jobNo, job?.company_id, job?.job_info_id, compacctnum]);

  const invoices = tabValue === 1 ? paidInvoices : openInvoices;

  useEffect(() => {
    const map = new Map();
    invoices.forEach((invoice) => {
      map.set(invoice.invoiceId, () => {
        setTickets(invoice.ticketsGli || []);
        setInfo({
          id: invoice.invoiceId,
          invoice_id: invoice.invoiceId,
          invoice: invoice.invoice,
          hauling: invoice.hauling,
          material: invoice.material,
          taxesMaterial: invoice.taxesMaterial,
          wait: invoice.wait,
          total: invoice.total,
          balance: invoice.balance,
          dueDate: invoice.dueDate,
          dateCreated: invoice.dateCreated,
          notes: invoice.notes,
          discount: invoice.discount,
          reconcile: invoice.reconcile,
        });
        openModalViewInvoice.current?.(false);
      });
    });
    setShowInvoiceInApply(map);
  }, [invoices]);

  const displayed = sorting.column
    ? sortInvoices(invoices, sorting.column, sorting.order)
    : invoices;
  const balanceTotal = displayed.reduce((sum, invoice) => sum + parseMoney(invoice.balance), 0);
  const invoiceAmountTotal = displayed.reduce((sum, invoice) => sum + parseMoney(invoice.total), 0);

  const toggleSort = (column) => {
    setSorting((current) => ({
      column,
      order: current.column === column && current.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIcon = (column) => {
    if (sorting.column !== column) return null;
    return sorting.order === 'asc' ? (
      <ArrowUpwardIcon fontSize='8px' />
    ) : (
      <ArrowDownwardIcon fontSize='8px' />
    );
  };

  const openView = (invoice) => {
    setTickets(invoice.ticketsGli || []);
    setInfo({
      id: invoice.invoiceId,
      invoice_id: invoice.invoiceId,
      invoice: invoice.invoice,
      hauling: invoice.hauling,
      material: invoice.material,
      taxesMaterial: invoice.taxesMaterial,
      wait: invoice.wait,
      total: invoice.total,
      balance: invoice.balance,
      dueDate: invoice.dueDate,
      dateCreated: invoice.dateCreated,
      notes: invoice.notes,
    });
    openModalViewInvoice.current?.(false);
  };

  const openEdit = (invoice) => {
    setTickets(invoice.ticketsGli || []);
    setInfo({
      invoice: invoice.invoice,
      hauling: invoice.hauling,
      material: invoice.material,
      taxesMaterial: invoice.taxesMaterial,
      wait: invoice.wait,
      total: invoice.total,
      balance: invoice.balance,
      dueDate: invoice.dueDate,
      dateCreated: invoice.dateCreated,
      notes: invoice.notes,
      discount: invoice.discount,
      reconcile: invoice.reconcile,
    });
    openModalEditInvoice.current?.(false);
  };

  const openPay = async (invoice) => {
    if (!compacctnum || payingId) return;
    setPayingId(invoice.invoiceId);
    try {
      const result = await client.get(`/invoices/get-apply/${compacctnum}`);
      const clients = Array.isArray(result?.data) ? result.data : [];
      const prepared = clients.map((clientRow) => ({
        ...clientRow,
        clientSelected: String(clientRow.clientId) === String(customerId),
        selectAll: false,
        payments: (clientRow.payments || []).map((payment) => ({
          ...payment,
          select: String(payment.invoiceId) === String(invoice.invoiceId),
        })),
      }));
      const selected = prepared.find((clientRow) => clientRow.clientSelected);
      const invoiceSelected = selected?.payments?.some((payment) => payment.select);
      if (!selected || !invoiceSelected) {
        notify(t('NO_INVOICES_READY_TO_PAY'), 'warning');
        return;
      }
      setDataToPayments(prepared);
      setPayOpen(true);
    } catch (error) {
      notify(t('NO_INVOICES_READY_TO_PAY'), 'error');
    } finally {
      setPayingId(null);
    }
  };

  const requestCustomerCsv = async () => {
    const jobId = job?.job_info_id;
    if (!compacctnum || jobId == null || csvLoading) return;
    setCsvLoading(true);
    try {
      const response = await client.post(
        `/invoices/customer/report/${compacctnum}/${jobId}`,
        {},
        { responseType: 'blob' }
      );
      const blob = response?.data;
      if (!blob || response.status === 204 || blob.size === 0) {
        notify(t('NO_INFORMATION_WAS_FOUND_MESSAGE'), 'error');
        return;
      }
      if (String(blob.type || '').includes('json')) {
        notify(t('NO_INFORMATION_WAS_FOUND_MESSAGE'), 'error');
        return;
      }
      const filename = csvFilenameFromResponse(
        response,
        `customer-invoices-${jobLabel || jobId}.csv`
      );
      setCsvFile({ blob, filename });
      setCsvOpen(true);
    } catch (error) {
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } finally {
      setCsvLoading(false);
    }
  };

  const invoicePdfProps = {
    companyLogoSrc,
    companyName: sessionCompany?.compname || '',
    companyAddress,
    customerName: job?.customer_name || '',
    jobLabel,
    jobNoLabel: t('JOB_NO'),
    projectLabel: t('PROJECT_NAME'),
    projectName: job?.j_destination?.l_name || '-',
    openInvoices,
    paidInvoices,
    labels: {
      openInvoices: t('OPEN_INVOICES'),
      paidInvoices: t('PAID_INVOICES'),
      invoiceNo: t('INVOICE_NO'),
      jobNo: t('JOB_NO'),
      dueDate: t('DUE_DATE'),
      invoiceAmount: t('INVOICE_AMOUNT'),
      balance: t('BALANCE'),
      total: t('TOTAL'),
      empty: t('NO_INVOICES_FOUND'),
      poweredBy: t('POWERED_BY'),
    },
  };

  const confirmPrint = async () => {
    if (printing) return;
    const previewWindow = window.open('', '_blank');
    setPrintChoiceOpen(false);
    setPrinting(true);
    try {
      await printCustomerInvoicesPdf({
        previewWindow,
        printScope,
        ...invoicePdfProps,
      });
    } catch (error) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      notify(t('SOMETHING_WENT_WRONG'), 'error');
    } finally {
      setPrinting(false);
    }
  };

  const downloadCustomerCsv = async () => {
    if (!csvFile?.blob) return;
    const saved = await saveBlobWithDialog(csvFile.blob, csvFile.filename);
    if (saved) {
      setCsvOpen(false);
      setCsvFile(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
        <DialogTitle sx={{ pr: 26 }}>
          Customers Invoices
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            {job?.customer_name || ''}
            {jobLabel != null && jobLabel !== '' ? ` · ${t('JOB_NO')} ${jobLabel}` : ''}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            {t('PROJECT_NAME')}: {job?.j_destination?.l_name || '-'}
          </Typography>
          <Box
            sx={{
              position: 'absolute',
              right: 48,
              top: 8,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip title={t('PRINT')}>
              <span>
                <IconButton
                  aria-label={t('PRINT')}
                  disabled={loading || printing}
                  onClick={() => setPrintChoiceOpen(true)}
                  sx={{ width: 40, height: 40 }}
                >
                  {printing ? <CircularProgress size={22} /> : <PrintIcon sx={{ fontSize: 28 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('SEND_EMAIL')}>
              <span>
                <IconButton
                  aria-label={t('SEND_EMAIL')}
                  disabled={loading || openInvoices.length + paidInvoices.length === 0}
                  onClick={() => setEmailOpen(true)}
                  sx={{ width: 40, height: 40 }}
                >
                  <EmailIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title='Export CSV'>
              <IconButton
                aria-label='Export CSV'
                disabled={csvLoading}
                onClick={requestCustomerCsv}
              >
                <svg xmlns='http://www.w3.org/2000/svg' height='32' viewBox='0 -960 960 960' width='32'>
                  <path d='M230-360h120v-60H250v-120h100v-60H230q-17 0-28.5 11.5T190-560v160q0 17 11.5 28.5T230-360Zm156 0h120q17 0 28.5-11.5T546-400v-60q0-17-11.5-31.5T506-506h-60v-34h100v-60H426q-17 0-28.5 11.5T386-560v60q0 17 11.5 30.5T426-456h60v36H386v60Zm264 0h60l70-240h-60l-40 138-40-138h-60l70 240ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H160v480Zm0 0v-480 480Z' />
                </svg>
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton
            aria-label='close'
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tabValue}
            onChange={(event, value) => setTabValue(value)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label={t('OPEN_INVOICES')} />
            <Tab label={t('PAID_INVOICES')} />
          </Tabs>
          {loading ? <LinearProgress sx={{ mb: 1 }} /> : null}
          {!loading && displayed.length === 0 ? (
            <Typography sx={{ py: 4, textAlign: 'center' }}>{t('NO_INVOICES_FOUND')}</Typography>
          ) : (
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={headerCellSx}
                      onClick={() => toggleSort('invoice')}
                    >
                      {t('INVOICE_NO')}
                      {sortIcon('invoice')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={headerCellSx}
                      onClick={() => toggleSort('job')}
                    >
                      {t('JOB_NO')}
                      {sortIcon('job')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={headerCellSx}
                      onClick={() => toggleSort('dueDate')}
                    >
                      {t('DUE_DATE')}
                      {sortIcon('dueDate')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={headerCellSx}
                      onClick={() => toggleSort('invoiceAmount')}
                    >
                      {t('INVOICE_AMOUNT')}
                      {sortIcon('invoiceAmount')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={headerCellSx}
                      onClick={() => toggleSort('balancePending')}
                    >
                      {t('BALANCE')}
                      {sortIcon('balancePending')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={{ borderColor: 'black', borderBottom: 2, backgroundColor: 'white' }}
                    >
                      {t('EDIT')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={{ borderColor: 'black', borderBottom: 2, backgroundColor: 'white' }}
                    >
                      {t('VIEW')}
                    </TableCell>
                    <TableCell
                      align='center'
                      className='text-center py-12 top-head'
                      sx={{ borderColor: 'black', borderBottom: 2, backgroundColor: 'white' }}
                    >
                      {t('PAY')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayed.map((invoice) => (
                    <TableRow key={invoice.invoiceId || invoice.invoice}>
                      <TableCell align='center' size='small' padding='none'>
                        {invoice.invoice}
                      </TableCell>
                      <TableCell align='center' size='small' padding='none'>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 'bold',
                            cursor: invoice.jobId ? 'pointer' : 'default',
                            width: 'fit-content',
                            margin: '0 auto',
                            '&:hover': invoice.jobId ? { textDecoration: 'underline' } : undefined,
                          }}
                          onClick={() => {
                            if (invoice.jobId) history.push(`/jobs/ticket/${invoice.jobId}`);
                          }}
                        >
                          {`${invoice.job}`}
                        </Typography>
                        <OriginalJobLabel job={invoice} variant='compact' />
                      </TableCell>
                      <TableCell align='center' size='small' padding='none'>
                        {invoice.dueDate ? moment(invoice.dueDate).format('MM/DD/YYYY') : ''}
                      </TableCell>
                      <TableCell align='right' size='small' padding='none'>
                        {moneyWithSign(invoice.total)}
                      </TableCell>
                      <TableCell align='right' size='small' padding='none'>
                        {moneyWithSign(invoice.balance)}
                      </TableCell>
                      <TableCell align='center' size='small' padding='none'>
                        <Tooltip title={t('EDIT_INVOICE')} placement='top'>
                          <IconButton onClick={() => openEdit(invoice)}>
                            <img src='/assets/images/edit.svg' alt='' style={{ height: '20px' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align='center' size='small' padding='none'>
                        {hasPermission(PERMISSION_MODEL.customersViewInvoice) && (
                          <Tooltip title={t('VIEW_INVOICE')} placement='top'>
                            <IconButton onClick={() => openView(invoice)}>
                              <PreviewIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align='center' size='small' padding='none'>
                        {hasPermission(PERMISSION_MODEL.customersApplyPayment) && (
                          <Tooltip title={t('PAY')} placement='top'>
                            <span>
                              <IconButton
                                disabled={
                                  changeCurrency(invoice?.balance ? invoice.balance : '$0.00') ===
                                    0 || payingId === invoice.invoiceId
                                }
                                onClick={() => openPay(invoice)}
                              >
                                <LocalAtmIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter
                  style={{ position: 'sticky', bottom: 0, zIndex: 2, backgroundColor: 'white' }}
                >
                  <TableRow>
                    <TableCell sx={{ borderColor: 'black', borderTop: 2 }} align='center'>
                      <Typography color='black' fontSize={14} sx={{ fontWeight: 'bold' }}>
                        {t('TOTAL')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                    <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                    <TableCell
                      align='start'
                      sx={{ padding: '0px 10px', borderColor: 'black', borderTop: 2 }}
                    >
                      <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} color='black' fontSize={13}>
                        {moneyWithSign(formatter.format(invoiceAmountTotal))}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align='start'
                      sx={{ padding: '0px 10px', borderColor: 'black', borderTop: 2 }}
                    >
                      <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }} color='black' fontSize={13}>
                        {moneyWithSign(formatter.format(balanceTotal))}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                    <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                    <TableCell sx={{ borderColor: 'black', borderTop: 2 }} />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={printChoiceOpen}
        onClose={() => setPrintChoiceOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{t('PRINT')}</DialogTitle>
        <DialogContent>
          <RadioGroup value={printScope} onChange={(event) => setPrintScope(event.target.value)}>
            <FormControlLabel value='open' control={<Radio />} label={t('OPEN_INVOICES')} />
            <FormControlLabel value='paid' control={<Radio />} label={t('PAID_INVOICES')} />
            <FormControlLabel value='both' control={<Radio />} label={t('BOTH')} />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintChoiceOpen(false)}>{t('CANCEL')}</Button>
          <Button variant='contained' onClick={confirmPrint} disabled={printing}>
            {t('PRINT')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={csvOpen} onClose={() => setCsvOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>{t('EXPORT_CSV')}</DialogTitle>
        <DialogContent>
          <Typography>{csvFile?.filename}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvOpen(false)}>{t('CANCEL')}</Button>
          <Button variant='contained' onClick={downloadCustomerCsv}>
            {t('DOWNLOAD')}
          </Button>
        </DialogActions>
      </Dialog>
      {emailOpen ? (
        <CustomerInvoicesEmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          compacctnum={compacctnum}
          jobId={job?.job_info_id}
          filename={`customer-invoices-${jobLabel || job?.job_info_id || 'job'}.pdf`}
          customerName={job?.customer_name || ''}
          jobLabel={jobLabel}
          createPdf={(printScope) =>
            buildCustomerInvoicesPdfBlob({ ...invoicePdfProps, printScope })
          }
          notify={notify}
        />
      ) : null}
      <ModalGenerateInvoices
        prop_data={propData}
        tickets={tickets}
        openModalInvoices={openModalViewInvoice}
        info={info}
      />
      <ModalToInvoice
        prop_data={propData}
        tickets={tickets}
        openModal={openModalEditInvoice}
        info={info}
        rechargedAfterChanges={rechargedAfterChanges}
        getPerCustomer={() => reloadRef.current?.()}
      />
      <ModalToApplyPayments
        prop_data={propData}
        open={payOpen}
        setOpen={setPayOpen}
        dataToPayments={dataToPayments}
        setDataToPayments={setDataToPayments}
        customerId={customerId}
        rechargedAfterChanges={rechargedAfterChanges}
        setMessage={setMessage}
        showInvoiceInApply={showInvoiceInApply}
        setShowInvoiceInApply={setShowInvoiceInApply}
        tickets={tickets}
        setTickets={setTickets}
        setInfo={setInfo}
        info={info}
        openModalViewInvoice={openModalViewInvoice}
        isSubContractor={false}
        labelToSelect='Customers'
      />
    </>
  );
}

export default JobCustomerInvoicesDialog;
